import React from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Rules = () => {
  const categories = [
    {
      title: 'Asian Cinema (Feature)',
      rules: [
        'Films completed on or after September 1, 2024 are eligible.',
        'Films submitted in earlier editions of GAFF are not eligible.',
        'Only films with a duration of more than 70 minutes are eligible.',
        'Entries accepted in all Asian languages (with English subtitles).',
        'Open to filmmakers from across Asia, and non-Asian filmmakers whose films highlight Asian culture.',
        'For non-Asian filmmakers, at least one producer must be of Asian origin.',
        'Indian censor certificate not required, but selected films are subject to CBFC clearance.'
      ]
    },
    {
      title: 'Indian Showcase (Competition & Non-Competition)',
      rules: [
        'Films must be in any Indian language (with English subtitles).',
        'Duration: more than 70 minutes.',
        'Films completed on or after January 1, 2025 are eligible.',
        'Films submitted in earlier editions of GAFF are not eligible.',
        'In exceptional cases, a non-competitive film may be moved to competition with jury recommendation and filmmaker\'s consent.'
      ]
    },
    {
      title: 'North East India Cinema',
      rules: [
        'Films must be in North East Indian languages (with English subtitles).',
        'Duration: more than 70 minutes.',
        'Films completed on or after January 1, 2025 are eligible.',
        'Films submitted in earlier editions of GAFF are not eligible.',
        '3 selected films from this category will compete in Indian Showcase Competition; others in non-competitive section.'
      ]
    },
    {
      title: 'Indian Shorts Spotlight',
      rules: [
        'Films must be in any Indian language (with English subtitles).',
        'Duration: less than 30 minutes (including end credits).',
        'Films completed on or after January 1, 2025 are eligible.'
      ]
    },
    {
      title: 'Asian Shorts Spotlight',
      rules: [
        'Films completed on or after September 1, 2024 are eligible.',
        'Films submitted in earlier GAFF editions are not eligible.',
        'Duration: less than 30 minutes.',
        'Entries accepted in all Asian languages (with English subtitles).',
        'Open to Asian filmmakers and non-Asian filmmakers focusing on Asian culture.',
        'For non-Asian filmmakers, at least one producer must be of Asian origin.',
        'Indian censor certificate not required, but selected films are subject to CBFC clearance.'
      ]
    }
  ];

  const generalRules = [
    'All entry fees are non-refundable under any circumstances.',
    'Selected films must confirm participation and submit required promotional materials within 7 days of notification.',
    'Once confirmed, selected films cannot be withdrawn.',
    'Upon selection, submitters must provide both DCP and MP4 files with English subtitles.',
    'All selection decisions are final, and GAFF is not obligated to provide feedback.',
    'Submitted films must not contain any content that infringes or violates copyright.',
    'The Festival reserves the right to exclude films that: (a) Do not align with festival aims, (b) May offend national sentiments or sensitivities, (c) Promote racial discrimination.',
    'GAFF reserves the right to use posters, video clips, and trailers of selected films for promotional purposes.',
    'By submitting, the applicant confirms they are the rightful owner or have authority to submit the film and indemnifies GAFF against any legal claims.',
    'For films produced by organizations/institutions, written permission via email from the concerned authority is required upon selection.'
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Asian Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff0000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 border-2 border-red-500/30 rounded-full"></div>
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-red-500/10 rounded-lg rotate-12"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="mb-8">
              <span className="inline-block px-6 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-500 text-sm uppercase tracking-widest mb-6">
                Submission Guidelines
              </span>
            </div>
            <h1 className="text-7xl md:text-9xl font-bold mb-8 leading-tight">
              RULES & <span className="text-red-500">REGULATIONS</span>
            </h1>
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-16 h-1 bg-gradient-to-r from-transparent to-red-500"></div>
              <div className="w-8 h-1 bg-red-500"></div>
              <div className="w-16 h-1 bg-gradient-to-l from-transparent to-red-500"></div>
            </div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Please read the submission guidelines carefully before submitting your film
            </p>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black relative">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-16">
            {categories.map((category, index) => (
              <div
                key={index}
                className="relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-12 hover:border-red-500/30 transition-all duration-500"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                {/* Category Number */}
                <div className="absolute -top-6 -left-6 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-2xl font-bold">
                  {index + 1}
                </div>

                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-red-500 uppercase tracking-wide">
                  {category.title}
                </h2>

                <div className="space-y-4">
                  {category.rules.map((rule, ruleIndex) => (
                    <div key={ruleIndex} className="flex items-start gap-4 group">
                      <CheckCircle2 size={24} className="text-red-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                      <p className="text-gray-300 text-lg leading-relaxed">
                        {rule}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </section>

      {/* General Rules Section */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-950 relative overflow-hidden">
        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, #ff0000 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-1 bg-red-500"></div>
                  <AlertCircle size={32} className="text-red-500" />
                  <div className="w-12 h-1 bg-red-500"></div>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold mb-6">
                GENERAL SUBMISSION <span className="text-red-500">RULES</span>
              </h2>
              <p className="text-gray-400 text-lg">Applicable to All Categories</p>
            </div>

            <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-3xl p-8 md:p-12 backdrop-blur-sm">
              <div className="space-y-6">
                {generalRules.map((rule, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-red-500/30 transition-colors">
                      <span className="text-red-500 font-bold text-sm">{index + 1}</span>
                    </div>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      {rule}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-12 text-center">
              <p className="text-xl text-gray-400">
                For any queries, please contact us at{' '}
                <a href="mailto:contact@gaff.co.in" className="text-red-500 hover:text-red-400 transition-colors font-semibold">
                  contact@gaff.co.in
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Submit Button Section */}
      <section className="py-32 bg-gradient-to-b from-zinc-950 to-black relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0 L50 100 M0 50 L100 50' stroke='%23ff0000' stroke-width='0.5' fill='none'/%3E%3Ccircle cx='50' cy='50' r='30' stroke='%23ff0000' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-12">
              <h2 className="text-5xl md:text-7xl font-bold mb-6">
                READY TO <span className="text-red-500">SUBMIT</span>?
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                Share your story with audiences across Asia
              </p>
            </div>
            
            <a
              href="https://filmfreeway.com/GuwahatiAsianFilmFestival"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-red-600 to-red-500 text-white text-xl font-bold uppercase tracking-wider rounded-xl hover:from-red-500 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-500/30 hover:shadow-red-500/50"
            >
              <span>SUBMIT NOW</span>
              <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>

            <p className="mt-8 text-gray-500 text-sm">
              All submissions are processed through FilmFreeway
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Rules;
